import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { updateConsent, getStoredConsent } from "../../utils/gtm";

/**
 * RGPD cookie consent banner.
 *
 * Visible only when:
 *  - appSettings.analytics_enabled === true
 *  - appSettings.cookie_banner_enabled === true
 *  - no consent decision has been persisted yet
 *
 * Text is taken from `appSettings.cookie_banner_text_{lang}` if defined,
 * otherwise falls back to i18n keys (`cookie_banner.message` etc.).
 */
export default function CookieBanner() {
  const { t, i18n } = useTranslation();
  const appSettings = useSelector((s) => s.settings.appSettings);
  const selectedLang = useSelector((s) => s.language && s.language.selectedLang);
  const [visible, setVisible] = useState(false);

  const lang = selectedLang || i18n.language || "fr";
  const analyticsOn = !!(appSettings && appSettings.analytics_enabled);
  const bannerOn = !!(appSettings && appSettings.cookie_banner_enabled);
  const policyUrl =
    appSettings && appSettings[`cookie_policy_url_${lang}`]
      ? appSettings[`cookie_policy_url_${lang}`]
      : "";
  const customMessage =
    appSettings && appSettings[`cookie_banner_text_${lang}`]
      ? appSettings[`cookie_banner_text_${lang}`]
      : "";

  useEffect(() => {
    if (!analyticsOn || !bannerOn) {
      setVisible(false);
      return;
    }
    const stored = getStoredConsent();
    setVisible(stored === null);
  }, [analyticsOn, bannerOn]);

  if (!visible) return null;

  const decide = (granted) => {
    updateConsent(granted);
    setVisible(false);
  };

  const message = customMessage || t("cookie_banner.message", {
    defaultValue:
      "Nous utilisons des cookies pour analyser le trafic et améliorer votre expérience sur notre site de réservation. Vous pouvez accepter, refuser, ou consulter notre politique de gestion des cookies.",
  });
  const labelAccept = t("cookie_banner.accept", { defaultValue: "Tout accepter" });
  const labelRefuse = t("cookie_banner.refuse", { defaultValue: "Refuser" });
  const labelLearn = t("cookie_banner.learn_more", { defaultValue: "En savoir plus" });

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie_banner.aria", { defaultValue: "Gestion des cookies" })}
      style={styles.wrapper}
    >
      <div style={styles.inner}>
        <p style={styles.message}>
          {message}
          {policyUrl ? (
            <>
              {" "}
              <a href={policyUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                {labelLearn}
              </a>
            </>
          ) : null}
        </p>
        <div style={styles.actions}>
          <button type="button" onClick={() => decide(false)} style={styles.btnGhost}>
            {labelRefuse}
          </button>
          <button type="button" onClick={() => decide(true)} style={styles.btnPrimary}>
            {labelAccept}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: 16,
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
  },
  inner: {
    background: "#1a1a1a",
    color: "#fafafa",
    padding: "16px 20px",
    borderRadius: 10,
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxWidth: 920,
    width: "100%",
    pointerEvents: "auto",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    fontSize: 14,
    lineHeight: 1.5,
  },
  message: {
    margin: 0,
  },
  link: {
    color: "#d6b87c",
    textDecoration: "underline",
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  btnGhost: {
    background: "transparent",
    color: "#fafafa",
    border: "1px solid rgba(250,250,250,0.4)",
    padding: "8px 18px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
  },
  btnPrimary: {
    background: "#b7995d",
    color: "#1a1a1a",
    border: "none",
    padding: "8px 18px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
};
